import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FolderOpen, Plus, Pencil, Trash2, Check, X } from "lucide-react";

export default function GroupManager({ open, onOpenChange, groups, onChange }) {
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/contacts/groups", { name: newName.trim(), description: newDesc.trim() });
      toast.success("Group created");
      setNewName("");
      setNewDesc("");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (g) => {
    setEditId(g.group_id);
    setEditName(g.name);
    setEditDesc(g.description || "");
  };

  const handleUpdate = async () => {
    if (!editName.trim()) return;
    try {
      await api.put(`/contacts/groups/${editId}`, { name: editName.trim(), description: editDesc.trim() });
      toast.success("Group updated");
      setEditId(null);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (groupId) => {
    try {
      await api.delete(`/contacts/groups/${groupId}`);
      toast.success("Group deleted");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid="group-manager-dialog">
        <DialogHeader>
          <DialogTitle data-testid="group-manager-title">Manage groups</DialogTitle>
        </DialogHeader>

        {/* Create new group */}
        <form onSubmit={handleCreate} className="space-y-2 pb-4 border-b border-zinc-100" data-testid="create-group-form">
          <Label className="text-xs font-medium text-zinc-600">New group</Label>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Group name"
            className="h-9"
            data-testid="new-group-name"
          />
          <Input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            className="h-9 text-sm"
            data-testid="new-group-desc"
          />
          <Button type="submit" size="sm" disabled={creating || !newName.trim()} data-testid="create-group-btn">
            <Plus size={14} className="mr-1.5" /> {creating ? "Creating..." : "Create group"}
          </Button>
        </form>

        {/* Group list */}
        <div className="space-y-1 mt-2">
          {groups.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4" data-testid="no-groups-message">
              No groups yet
            </p>
          ) : (
            groups.map((g) => (
              <div
                key={g.group_id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-zinc-50 group"
                data-testid={`group-item-${g.group_id}`}
              >
                {editId === g.group_id ? (
                  /* Edit mode */
                  <div className="flex-1 space-y-1.5">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                      data-testid="edit-group-name"
                    />
                    <Input
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      placeholder="Description"
                      className="h-8 text-sm"
                      data-testid="edit-group-desc"
                    />
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-7 text-xs" onClick={handleUpdate} data-testid="save-group-btn">
                        <Check size={12} className="mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditId(null)}>
                        <X size={12} className="mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Display mode */
                  <>
                    <FolderOpen size={16} className="text-zinc-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{g.name}</p>
                      {g.description && (
                        <p className="text-xs text-zinc-500 truncate">{g.description}</p>
                      )}
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500 shrink-0">
                      {g.contact_count || 0}
                    </span>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => startEdit(g)}
                        data-testid={`edit-group-${g.group_id}`}
                      >
                        <Pencil size={13} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-zinc-500 hover:text-red-500"
                        onClick={() => handleDelete(g.group_id)}
                        data-testid={`delete-group-${g.group_id}`}
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
